import { AsyncPipe } from "@angular/common";
import { Component, inject } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { ProductService } from "@domain/product/product.service";
import { KvmConfiguration } from "@domain/project/configurations/monitoring/kvm-configuration";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { MonitoringProject } from "@domain/project/monitoring-project";
import { ConfiguredProductComponent } from "@project/floorplanner/floorplan-menu/fgds/configured-product/configured-product.component";
import { FloorplanProductMenuComponent } from "@project/floorplanner/floorplan-menu/fgds/floorplan-product-menu/floorplan-product-menu.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { DraggedItemType, FloorplanDragDropService } from "@utils/floorplan-drag-drop.service";
import { catchError, map, Observable, of, shareReplay } from "rxjs";

@Component({
  selector: "app-kvm-menu",
  imports: [ConfiguredProductComponent, FloorplanProductMenuComponent, AsyncPipe],
  templateUrl: "./kvm-menu.component.html",
  styleUrl: "./kvm-menu.component.scss",
})
export class KvmMenuComponent {
  private floorplanService = inject(FloorplanService);
  private dragDropService = inject(FloorplanDragDropService);
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);
  private imageUrlByProductId = new Map<string, Observable<string | undefined>>();

  protected floorplan: Floorplan;

  protected readonly tooltipTextKvms: string = $localize`:@@global.kvms:KVMs`;

  constructor() {
    this.floorplan = this.floorplanService.selectedFloorplan;
  }

  protected get kvms(): readonly KvmConfiguration[] {
    if (this.floorplan.project instanceof MonitoringProject) {
      return this.floorplan.project.kvms;
    }
    return [];
  }

  protected addKvm(kvmConfig: KvmConfiguration) {
    this.floorplanService.addKvm(kvmConfig);
  }

  protected addPlaceholder() {
    this.floorplanService.addKvmPlaceholder();
  }

  protected deleteKvm(kvmConfig: KvmConfiguration) {
    (this.floorplan.project as MonitoringProject).deleteKvm(kvmConfig);
  }

  protected drag(kvmConfig: KvmConfiguration) {
    this.dragDropService.setDragged({ type: DraggedItemType.KVM, config: kvmConfig });
  }

  protected getFallbackImageUrl(config: KvmConfiguration) {
    return config.transmitterGroupImageCantoId
      ? ProductImage.getCantoSourceUrl(config.transmitterGroupImageCantoId)
      : "./assets/floorplan-menu-icons/kvm_menu.svg";
  }

  protected getImageUrl$(config: KvmConfiguration): Observable<string | undefined> {
    const productId = config.transmitterProductId;
    if (!productId) {
      return of(undefined);
    }

    const cached = this.imageUrlByProductId.get(productId);
    if (cached) {
      return cached;
    }

    const image$ = this.monitoringProductService
      .loadDeviceFromDB(productId, MonitoringCategory.KVMS, this.productService.getFilterCountry())
      .pipe(
        map((device) => (device?.productImageCantoID ? ProductImage.getCantoSourceUrl(device.productImageCantoID) : undefined)),
        catchError(() => of(undefined)),
        shareReplay(1),
      );

    this.imageUrlByProductId.set(productId, image$);
    return image$;
  }

  protected dragPlaceholder() {
    this.dragDropService.setDragged({ type: DraggedItemType.KVM_PLACEHOLDER });
  }

  protected updateKvmsItemTextVisibility(visible: boolean) {
    this.floorplan.floorplanState.updateKvmsItemTextVisibility(visible);
  }
}
